import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    TextField,
    Skeleton,
} from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import "../css/Charts.css";
import { dashboardService } from "../services/dashboardService";
import { userServices } from "../services/userServices";
import { teamServices } from "../services/teamServices";

// Colors copied from your dashboard file for consistency
const COLORS = ["#42a5f5", "#ffa726", "#66bb6a", "#F5D327"];
const HOURS_COLORS = {
    "Completed": "#66bb6a",
    "Not Completed": "#ffa726",
};

const monthLabels = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const generateMonthYearOptions = (monthsBack = 6) => {
    const now = new Date();
    const opts = [];
    for (let i = 0; i < monthsBack; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const y = d.getFullYear();
        const m = d.getMonth() + 1; // 1..12
        const label = `${monthLabels[d.getMonth()]} ${y}`;
        const value = `${y}-${String(m).padStart(2, "0")}`; // "2025-02"
        opts.push({ value, label, month: m, year: y });
    }
    return opts;
};

const BillableHoursPieChart = ({ roles, currentUserID }) => {
    const isLeaderOrAdmin = roles?.includes("SUPER_ADMIN") || roles?.includes("ADMIN") || roles?.includes("SUB_ADMIN") || roles?.includes("MANAGE_JOBS");
    const isLeader = !roles?.includes("ADMIN") && !roles?.includes("SUPER_ADMIN") && roles?.includes("MANAGE_JOBS");
    const isAdmin = roles?.includes("SUPER_ADMIN") || roles?.includes("ADMIN");
    const isAdminOrSubAdmin = roles?.includes("SUPER_ADMIN") || roles?.includes("ADMIN") || roles?.includes("SUB_ADMIN");
    // const currentUserID = localStorage.getItem('userId');

    const monthOptions = useMemo(() => generateMonthYearOptions(6), []);

    const defaultMonthValue = monthOptions.length ? monthOptions[0].value : (() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    })();

    const [selectedMonths, setSelectedMonths] = useState([defaultMonthValue]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [teamUsers, setTeamUsers] = useState([]);
    const [clientCategory, setClientCategory] = useState("ADHOC");
    const [teams, setTeams] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState("ALL");
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSliceData, setSelectedSliceData] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [groupedClients, setGroupedClients] = useState([]);
    const [parentDialogOpen, setParentDialogOpen] = useState(false);
    const [childDialogOpen, setChildDialogOpen] = useState(false);
    const [childJobs, setChildJobs] = useState([]);
    const [selectedClientName, setSelectedClientName] = useState(null);
    const [loadingJobs, setLoadingJobs] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => {
            const value = searchQuery.trim();

            if (value === "" || value.length > 2) {
                setDebouncedSearch(searchQuery);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    const formatHoursToHHMM = (decimalHours) => {
        const hours = Math.floor(decimalHours);
        const minutes = Math.round((decimalHours - hours) * 60);
        return `${hours}:${minutes.toString().padStart(2, "0")}`;
    };

    const formatChargeHoursToHHMM = (decimalHours) => {
        if (decimalHours == null) return "00:00";
        return formatHoursToHHMM(decimalHours);
    };

    const formatBudgetHoursToHHMM = (decimalHours) => {
        if (decimalHours == null) return "00:00";
        const hours = Math.floor(decimalHours);
        const fractionalPart = decimalHours - hours;

        let minutes = Math.round(fractionalPart * 100);
        const extraHours = Math.floor(minutes / 60);

        minutes = minutes % 60;
        const totalHours = hours + extraHours;
        return `${String(totalHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    };

    const parseValueToDecimalHours = (value) => {
        if (typeof value === "string" && value.includes(":")) {
            const [h, m] = value.split(":").map(Number);
            return h + m / 60;
        } else {
            return Number(value) || 0;
        }
    };

    useEffect(() => {
        if (!isLeader) {
            setSelectedUser(currentUserID);
        } else {
            const fetchUsers = async () => {
                try {
                    const users = await userServices.getUsersByRoleName();
                    setTeamUsers(users);

                    if (isAdminOrSubAdmin && users.length > 0) {
                        setSelectedUser(users[0].id);
                    } else {
                        setSelectedUser(currentUserID);
                    }
                } catch (err) {
                    console.error("Failed to fetch team users", err);
                }
            };
            fetchUsers();
        }
    }, [isLeader, currentUserID]);

    useEffect(() => {
        if (isAdminOrSubAdmin) {
            const fetchTeams = async () => {
                const list = await teamServices.getAllTeamsListForUserScreen();
                setTeams(list);
            };
            fetchTeams();
        }
    }, [isAdminOrSubAdmin]);

    const selectedMonthNumbers = useMemo(() => {
        return selectedMonths.map(v => {
            const [y, m] = v.split("-").map(Number);
            return { year: y, month: m };
        });
    }, [selectedMonths]);

    const monthsArray = selectedMonthNumbers.map(m => m.month);
    const yearForQuery = selectedMonthNumbers[0]?.year;

    const fetchJobHoursSummaryCounts = async () => {
        try {
            setLoading(true);
            const payload = {
                userId: !isAdminOrSubAdmin ? selectedUser : null,
                teamId: isAdminOrSubAdmin && selectedTeam !== "ALL" ? selectedTeam : null,
                clientCategory,
                months: monthsArray,
                year: yearForQuery,
            };
            const data = await dashboardService.getJobHoursSummaryForDashboard(payload);

            const chartFormatted = Object.entries(data || {})
                .filter(([_, value]) => value) // ignore empty/null
                .map(([label, value], idx) => {
                    const hours = parseValueToDecimalHours(value);
                    return {
                        id: idx,
                        value: hours,
                        label,
                        display: formatHoursToHHMM(hours),
                        color: HOURS_COLORS[label] || COLORS[idx % COLORS.length],
                    };
                });

            setChartData(chartFormatted);
        } catch (err) {
            console.error("Failed to fetch job hours summary", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isAdminOrSubAdmin || selectedUser) {
            fetchJobHoursSummaryCounts();
        }
    }, [selectedUser, selectedTeam, clientCategory, selectedMonths]);

    // useEffect(() => {
    //     if (childDialogOpen && selectedClientName) {
    //         const currentClientGroup = groupedClients.find(c => c.clientName === selectedClientName);

    //         if (currentClientGroup) {
    //             setChildJobs(currentClientGroup.jobs);
    //         } else {
    //             setChildJobs([]);
    //         }
    //     }
    // }, [groupedClients, childDialogOpen, selectedClientName]);

    const fetchClientGroups = async (searchTerm = "") => {
        const completed = selectedSliceData === "Completed";
        try {
            setLoadingJobs(true);
            const result = await dashboardService.getJobsByCompletionStatusForDashboard(
                !isAdminOrSubAdmin ? selectedUser : null,
                isAdminOrSubAdmin && selectedTeam !== "ALL" ? selectedTeam : null,
                clientCategory,
                monthsArray,
                yearForQuery,
                completed,
                searchTerm // Pass search param
            );
            setGroupedClients(result);

            if (selectedClientName) {
                const currentClient = result.find(c => c.clientName === selectedClientName);
                setChildJobs(currentClient ? currentClient.jobs : []);
            }

        } catch (err) {
            console.error("Failed to fetch jobs by completion status", err);
            setGroupedClients([]);
            if (selectedClientName) setChildJobs([]);
        } finally {
            setLoadingJobs(false);
        }
    };

    useEffect(() => {
        if (parentDialogOpen && selectedSliceData) {
            fetchClientGroups(debouncedSearch);
        }
    }, [parentDialogOpen, selectedSliceData, debouncedSearch, selectedClientName]);

    const handleItemClick = async (event, pieItemIdentifier, item) => {
        const sliceLabel = item?.label;
        if (!sliceLabel) return;

        setSelectedSliceData(sliceLabel);
        setSearchQuery("");
        setDebouncedSearch("");
        setParentDialogOpen(true);
    };

    const formatChargeHours = (hoursDecimal) => {
        if (hoursDecimal == null) hoursDecimal = 0.0;
        let h = Math.floor(hoursDecimal);
        let m = Math.round((hoursDecimal - h) * 60);

        if (m === 60) {
            h += 1;
            m = 0;
        }
        return `${h}:${String(m).padStart(2, '0')}h`;
    };

    const formatBudgetHours = (hoursDecimal) => {
        if (hoursDecimal == null) hoursDecimal = 0;
        let h = Math.floor(hoursDecimal);
        let m = Math.round((hoursDecimal - h) * 100);

        h += Math.floor(m / 60);
        m = m % 60;

        return `${h}:${String(m).padStart(2, '0')}h`;
    };

    // Columns
    const columns = [
        { label: "Job Name", key: "name" },
        { label: "Job Type", key: "type" },
        { label: "Status", key: "status" },
        { label: "Spent Hours", key: "totalSpentTime" },
        ...(selectedSliceData === "Completed"
            ? [
                {
                    label: "Charge Hours",
                    key: "chargeHours",
                    render: (job) => (job.chargeHours ? formatChargeHoursToHHMM(job.chargeHours) : "00:00"),
                },
            ]
            : [
                {
                    label: "Budget Hours",
                    key: "budgetHours",
                    render: (job) => (job.budgetHours ? formatBudgetHoursToHHMM(job.budgetHours) : "00:00"),
                },
            ]),
        {
            label: "Job Admin",
            key: "assignedUser",
            render: (job) => `${job.assignedUser?.firstName || ""} ${job.assignedUser?.lastName || ""}`.trim(),
        },
    ];

    return (
        <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, justifyContent: 'center', alignItems: 'center' }}>

                {/* CATEGORY DROPDOWN */}
                <FormControl sx={{ flex: { xs: "1 1 100%", sm: 1 }, minWidth: 130 }} size="small">
                    <InputLabel>Client Category</InputLabel>
                    <Select value={clientCategory} label="Client Category"
                        onChange={(e) => setClientCategory(e.target.value)}>
                        <MenuItem value="ADHOC">Adhoc</MenuItem>
                        <MenuItem value="FTE">FTE</MenuItem>
                    </Select>
                </FormControl>

                {isLeader && teamUsers.length > 0 && (
                    <FormControl sx={{ flex: 1, minWidth: 120 }} size="small">
                        <InputLabel>Select User</InputLabel>
                        <Select
                            value={(Array.isArray(teamUsers) && teamUsers.some((u) => String(u.id) === String(selectedUser))) ? selectedUser : ''}
                            label="Select User"
                            onChange={(e) => setSelectedUser(e.target.value)}
                        >
                            {teamUsers.map((u) => (
                                <MenuItem key={u.id} value={u.id}>
                                    {u.firstName} {u.lastName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {isAdminOrSubAdmin && (
                    <FormControl sx={{ flex: 1, minWidth: 120 }} size="small">
                        <InputLabel>Select Team</InputLabel>
                        <Select
                            value={(selectedTeam === 'ALL' || (Array.isArray(teams) && teams.some((t) => {
                                const ownerIdVal = typeof t.ownerId === 'object' ? t.ownerId?.id : t.ownerId;
                                return String(ownerIdVal) === String(selectedTeam);
                            }))) ? selectedTeam : 'ALL'}
                            label="Select Team"
                            onChange={(e) => setSelectedTeam(e.target.value)}
                        >
                            <MenuItem value="ALL">All Teams</MenuItem>

                            {teams.map((t) => {
                                const ownerIdVal = typeof t.ownerId === 'object' ? t.ownerId?.id : t.ownerId;
                                return (
                                    <MenuItem key={t.id} value={ownerIdVal}>
                                        {t.name}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                )}

                <FormControl sx={{ flex: 1, minWidth: 120 }} size="small">
                    <InputLabel>Select Month</InputLabel>
                    <Select value={selectedMonths} label="Select Month" multiple
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value.length === 0) {
                                setSelectedMonths([defaultMonthValue]);
                                return;
                            }
                            setSelectedMonths(value);
                        }}
                        renderValue={(selected) =>
                            selected
                                .map(v => monthOptions.find(opt => opt.value === v)?.label)
                                .join(", ")
                        }
                        sx={{
                            maxWidth: { xs: 'none', sm: 150 }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                    >
                        {monthOptions.map((opt) => {
                            const isSelected = selectedMonths.includes(opt.value);
                            return (
                                <MenuItem key={opt.value} value={opt.value} sx={{ fontWeight: isSelected ? "bold" : "normal" }}>
                                    {opt.label}
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
            </Box>

            <Box className="chart-container">
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 6,
                            gap: 2,
                        }}
                    >
                        <Skeleton variant="circular" width={170} height={170} />
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="42%" />
                    </Box>
                ) : chartData.length === 0 ? (
                    <Typography align="center" color="text.secondary" sx={{ height: 250, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        No Job data available
                    </Typography>
                ) : (
                    <Box className="chart-pie-box">
                        <PieChart
                            series={[
                                {
                                    data: chartData,
                                    valueFormatter: (v) => `${v.display}h`,
                                    arcLabel: (arc) => {
                                        const total = chartData.reduce((s, i) => s + i.value, 0);
                                        const percentage = total ? arc.value / total : 0;
                                        return percentage > 0.05 ? `${arc.display}h` : "";
                                    },
                                    arcLabelRadius: "65%",
                                    highlightScope: { faded: "none", highlighted: "none" },
                                },
                            ]}
                            margin={{ top: 10, bottom: 40, left: 30, right: 30 }}
                            slotProps={{
                                legend: {
                                    direction: "row",
                                    position: { vertical: "bottom", horizontal: "middle" },
                                    padding: 0,
                                },
                            }}
                            tooltip={{ trigger: "item" }}
                            className="pie-chart"
                            onItemClick={handleItemClick}
                        />
                    </Box>
                )}
            </Box>

            {/* PARENT DIALOG - Client Groups */}
            <Dialog
                open={parentDialogOpen}
                onClose={() => setParentDialogOpen(false)}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: { className: "chart-dialog-paper" },
                }}
            >
                <DialogTitle component="div" className="chart-dialog-title">
                    <Typography variant="h6">
                        {selectedSliceData} jobs
                    </Typography>

                    <IconButton
                        aria-label="close"
                        onClick={() => setParentDialogOpen(false)}
                        className="chart-dialog-close-btn"
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="chart-dialog-content">
                    {loadingJobs ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <Box className="category-container">
                            {groupedClients.map((client, idx) => (
                                <Box
                                    key={idx}
                                    onClick={() => {
                                        setSelectedClientName(client.clientName);
                                        setChildJobs(client.jobs);
                                        setChildDialogOpen(true);
                                    }}
                                    className="category-item"
                                >
                                    {client.clientName} ({selectedSliceData === "Completed" ? formatChargeHours(client.totalHours) : formatBudgetHours(client.totalHours)})
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={childDialogOpen}
                onClose={() => {
                    setChildDialogOpen(false)
                    setSelectedClientName(null);
                    setSearchQuery("");
                    setDebouncedSearch("");
                }}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        className: "chart-dialog-paper",
                    },
                }}
            >
                <DialogTitle component="div" className="chart-dialog-title">
                    <Typography variant="h6">
                        {selectedClientName}
                    </Typography>

                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            setChildDialogOpen(false)
                            setSelectedClientName(null);
                            setSearchQuery("");
                            setDebouncedSearch("");
                        }}
                        className="chart-dialog-close-btn"
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent className="chart-dialog-content">
                    <Box className="chart-search-bar">
                        <TextField
                            size="small"
                            placeholder="Type to search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ width: 260 }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>

                    {loadingJobs ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : childJobs.length === 0 ? (
                        <Typography sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
                            No jobs found for this category.
                        </Typography>
                    ) : (
                        <TableContainer sx={{ flex: 1, maxHeight: "80vh" }}>
                            <Table size="small" stickyHeader className="chart-table">
                                <TableHead>
                                    <TableRow>
                                        {columns.map((col, idx) => (
                                            <TableCell key={idx}>
                                                <b>{col.label}</b>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {childJobs.map((job, index) => (
                                        <TableRow key={job.id || index}>
                                            {columns.map((col, idx) => (
                                                <TableCell key={idx} className="cell-overflow">
                                                    {col.render ? col.render(job) : job[col.key] ?? "-"}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default BillableHoursPieChart;